import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";

const checksum = z
  .string()
  .length(44)
  .refine((value) => {
    try {
      return atob(value).length === 32 && btoa(atob(value)) === value;
    } catch {
      return false;
    }
  }, "Expected canonical SHA-256 base64");
export const audioUploadCreateSchema = z.strictObject({
  contentType: z.enum(["audio/mpeg", "audio/x-m4a", "audio/wav", "audio/webm"]),
  byteLength: z.int().positive().max(209715200), // 200 MiB
  checksumSha256: checksum,
});
export type AudioUploadInput = z.output<typeof audioUploadCreateSchema>;
const identity = { id: z.uuid(), expiresAt: z.iso.datetime() };
const uploaded = z.object({ ...identity, status: z.literal("uploaded") });
export const audioUploadCreatedSchema = z.discriminatedUnion("status", [
  uploaded,
  z.object({
    ...identity,
    status: z.literal("pending"),
    upload: z.object({
      method: z.literal("PUT"),
      url: z.url({ protocol: /^https$/ }),
      expiresAt: z.iso.datetime(),
      headers: z.object({
        "content-type": audioUploadCreateSchema.shape.contentType,
        "if-none-match": z.literal("*"),
        "x-amz-checksum-sha256": checksum,
        "x-amz-sdk-checksum-algorithm": z.literal("SHA256"),
      }),
    }),
  }),
]);
export const audioUploadSchema = z.object({
  ...identity,
  ...audioUploadCreateSchema.pick({ contentType: true, byteLength: true })
    .shape,
  status: z.enum(["pending", "uploaded", "expired"]),
  completedAt: z.iso.datetime().nullable(),
});
const protectedUpload = oc.errors({
  BAD_REQUEST: { message: "Invalid request" },
  UNAUTHORIZED: { message: "Authentication required" },
  FORBIDDEN: { message: "Account required" },
  NOT_FOUND: { message: "Upload not found" },
  CONFLICT: { message: "Upload conflict" },
  METHOD_NOT_ALLOWED: { message: "Method Not Allowed" },
  INTERNAL_SERVER_ERROR: { message: "Internal Server Error" },
  SERVICE_UNAVAILABLE: { message: "Audio upload unavailable" },
});
const query = z.strictObject({}).default({});
const params = z.strictObject({ id: z.uuid().toLowerCase() });
const meta = (
  method: "POST" | "GET" | "PUT",
  path: string,
  successStatus = 200,
) =>
  openapi({
    method,
    path: `/api/v2${path}`,
    inputStructure: "detailed",
    successStatus,
    spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
  });
export const audioUploadContract = {
  createAudioUpload: protectedUpload
    .input(
      z.object({
        query,
        headers: z.object({ "idempotency-key": z.uuid().toLowerCase() }),
        body: audioUploadCreateSchema,
      }),
    )
    .meta(meta("POST", "/audio-uploads", 201))
    .output(audioUploadCreatedSchema),
  getAudioUpload: protectedUpload
    .input(z.object({ query, params }))
    .meta(meta("GET", "/audio-uploads/{id}"))
    .output(audioUploadSchema),
  completeAudioUpload: protectedUpload
    .input(z.object({ query, params, body: z.strictObject({}) }))
    .meta(meta("PUT", "/audio-uploads/{id}/completion"))
    .output(audioUploadSchema.extend({ status: z.literal("uploaded") })),
};
